<template>
  <div class="relative bg-[--header-bottom-bg-color]">
    <div v-if="catalogMenuVisible" class="bg-black/25 fixed w-full h-screen left-0 top-0 z-[-1]"></div>

    <!-- Fix to get shadow working correctly with z-index -->
    <div class="w-full h-[20px] shadow-md absolute bottom-0 z-30"></div>
    <nav
      ref="bottomHeader"
      class="border-b-1 relative z-[2] flex min-h-[5.5rem] flex-col items-center gap-x-5 border-[#DCDCDC] bg-[color:var(--color-header-bottom-bg)] py-8"
    >
      <div class="container mx-auto flex w-full max-w-screen-xl justify-between">
        <router-link to="/">
          <VcImage :src="$cfg.logo_image" :alt="$context.storeName" class="h-10 lg:h-12" width="160" height="48" />
        </router-link>
        <div class="flex items-center md:ml-4 min-w-[420px]">
          <InnoMegaMenu :title="$t('custom.header.catalog_title')" @toggle="catalogMenuVisible = !catalogMenuVisible" />

          <template v-for="(item, index) in mainMenuItems" :key="index">
            <a
              v-if="isExternalLink(item.route)"
              class="mr-4 cursor-pointer font-semibold text-[color:var(--color-primary)] hover:text-[color:var(--color-secondary-500)]"
              target="_blank"
              :href="item.route as string"
            >
              {{ item.title }}
            </a>

            <router-link
              v-else-if="item.route === '/acties'"
              class="mr-4 cursor-pointer rounded-lg bg-[color:var(--color-sale-badge-bg)] px-3 py-1 font-semibold text-white hover:bg-[color:var(--color-secondary-500)]"
              :to="item.route ?? '#'"
            >
              {{ item.title }}
            </router-link>

            <router-link
              v-else-if="item.route === '/nieuw'"
              class="mr-4 cursor-pointer rounded-lg bg-[color:var(--color-secondary-500)] px-3 py-1 font-semibold text-white hover:bg-[color:var(--color-primary-500)]"
              :to="item.route ?? '#'"
            >
              {{ item.title }}
            </router-link>

            <router-link
              v-else
              class="mr-4 cursor-pointer font-semibold text-[color:var(--color-primary)] hover:text-[color:var(--color-secondary-500)]"
              :to="item.route ?? '#'"
            >
              {{ item.title }}
            </router-link>
          </template>
        </div>
        <InnoSearchBar />

        <ul class="ml-2 flex items-center">
          <li v-for="item in desktopMainMenuItems" :key="item.id">
            <InnoBottomHeaderLink v-if="item.id === 'compare'" :link="item" :count="productsIds.length" />

            <InnoBottomHeaderLink
              v-else-if="item.id === 'login'"
              :link="isAuthenticated ? { ...item, route: '/account/dashboard' } : item"
            />

            <InnoBottomHeaderLink
              v-else-if="item.id === 'cart'"
              :link="item"
              :count="cart?.itemsQuantity"
              data-cart-icon
            />

            <InnoBottomHeaderLink v-else-if="item.id === 'push-messages'" :link="item" :count="unreadCountWithHidden" />

            <InnoBottomHeaderLink v-else :link="item" />
          </li>
        </ul>
      </div>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from "@vueuse/core";
import { ref, shallowRef } from "vue";
import { useNavigations } from "@/core/composables";
import { getLinkAttr } from "@/core/utilities";
import { usePushMessages } from "@/modules/push-messages/composables/usePushMessages";
import { useUser } from "@/shared/account/composables/useUser";
import { useShortCart } from "@/shared/cart";
import { useCompareProducts } from "@/shared/compare";
import { InnoSearchBar } from "@/shared/layout";
import InnoBottomHeaderLink from "./inno-bottom-header-link.vue";
import InnoMegaMenu from "./inno-mega-menu.vue";
import type { RouteLocationRaw } from "vue-router";

const { isAuthenticated } = useUser();
const { cart } = useShortCart();
const { desktopMainMenuItems, mainMenuItems } = useNavigations();
const { productsIds } = useCompareProducts();

const { unreadCountWithHidden } = usePushMessages();

const bottomHeader = ref<HTMLElement | null>(null);
const catalogMenuElement = shallowRef<HTMLElement | null>(null);
const navigation = shallowRef<HTMLElement | null>(null);
const catalogMenuVisible = ref(false);

function isExternalLink(link?: RouteLocationRaw) {
  return "externalLink" in getLinkAttr(link);
}

onClickOutside(
  catalogMenuElement,
  () => {
    catalogMenuVisible.value = false;
  },
  { ignore: [navigation] },
);
</script>
